/**
 * ChatDock — persistent concierge chat that follows the client across pages.
 *
 * Mounted in the Shell for both client roles so the conversation started on
 * the landing page is reachable from MyDossier, SignDocument, etc. The dock pulls from `shell/chat`, which is the *same* store the hero
 * chat on ClientWelcome uses — there is one conversation, two surfaces.
 *
 * Hidden on the new-client home (`/`) since the hero already shows the chat
 * at full size there; showing both would just be visual noise.
 *
 * Phase 0: CSS-only, no portal — the floating panel is pinned bottom-right.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { MessageSquare, Send, X } from "lucide-react";

import { useChat, useDockOpen, type ChatMessage } from "./chat";
import { useRoleView } from "./roleView";

export function ChatDock() {
  const { view } = useRoleView();
  const { pathname } = useLocation();
  const { messages, typing, send } = useChat();
  const [open, setOpen] = useDockOpen();
  const [draft, setDraft] = useState("");
  const [lastSeen, setLastSeen] = useState<number>(
    () => messages[messages.length - 1]?.id ?? 0,
  );
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Hide dock on the new-client landing — the hero chat is the chat there.
  const hideOnHome = view === "client-new" && pathname === "/";
  const visible = !hideOnHome;

  useEffect(() => {
    if (open) {
      bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
      setLastSeen(messages[messages.length - 1]?.id ?? 0);
      inputRef.current?.focus();
    }
  }, [open, messages]);

  if (!visible) return null;

  const lastMsg = messages[messages.length - 1];
  const unread =
    !open && lastMsg && lastMsg.role === "bot" && lastMsg.id > lastSeen ? 1 : 0;

  function handleSend() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    send(trimmed);
    setDraft("");
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          className="rp-dock-fab"
          onClick={() => setOpen(true)}
          aria-label="Open concierge chat"
        >
          <MessageSquare size={20} />
          <span>Ask me anything</span>
          {unread > 0 && <span className="rp-dock-fab__badge">{unread}</span>}
        </button>
      )}

      {open && (
        <div
          className="rp-dock-panel"
          role="dialog"
          aria-label="Concierge chat"
        >
          <div className="rp-dock-head">
            <div className="rp-dock-head__av" aria-hidden="true">
              <MessageSquare size={18} color="white" />
            </div>
            <div className="rp-dock-head__meta">
              <div className="rp-dock-head__name">Home concierge</div>
              <div className="rp-dock-head__status">
                Preview · quick answers
              </div>
            </div>
            <button
              type="button"
              className="rp-dock-head__close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              <X size={16} />
            </button>
          </div>

          <div
            className="rp-dock-body"
            ref={bodyRef}
            role="log"
            aria-live="polite"
          >
            {messages.map((m) => (
              <DockTurn key={m.id} message={m} />
            ))}
            {typing && (
              <div className="rp-dock-typing" aria-label="Assistant is typing">
                <span />
                <span />
                <span />
              </div>
            )}
          </div>

          <div className="rp-dock-composer">
            <label htmlFor="rp-dock-input" className="rp-dock-sr">
              Message the concierge
            </label>
            <input
              id="rp-dock-input"
              ref={inputRef}
              className="rp-dock-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSend();
              }}
              placeholder="Type a message…"
            />
            <button
              type="button"
              className="rp-dock-send"
              onClick={handleSend}
              disabled={!draft.trim()}
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function DockTurn({ message }: { message: ChatMessage }) {
  const isBot = message.role === "bot";
  return (
    <div
      className={`rp-dock-turn ${isBot ? "rp-dock-turn--bot" : "rp-dock-turn--user"}`}
    >
      <div
        className={`rp-dock-msg ${isBot ? "rp-dock-msg--bot" : "rp-dock-msg--user"}`}
      >
        {message.text}
      </div>
    </div>
  );
}
